import axios from 'axios';

import {
  endSessionForGeneration,
  getAccessToken,
  getAccessTokenForGeneration,
  getRefreshToken,
  getSessionRefreshAuthority,
  rotateAccessToken,
  setRefreshToken,
  type SessionRefreshAuthority,
} from './auth';

declare module 'axios' {
  export interface AxiosRequestConfig {
    fieldwatchSkipRateLimitRetry?: boolean;
    // Opt a single request OUT of the 401 auth-refresh-and-replay interceptor.
    // A request that must be exactly-once under a FIXED identity (e.g. a
    // self-only account-scoped write) sets this so a 401 rejects immediately
    // instead of being replayed after a token refresh — a refresh can replace
    // the session identity mid-flight and the replay would otherwise resend the
    // original body under a different user's token.
    fieldwatchSkipAuthRefresh?: boolean;
    // Stamped ONLY by the refresh interceptor on a replayed request: the
    // session generation (null = legacy custody) its adoption was validated
    // under. The request interceptor re-validates it at the last synchronous
    // point before dispatch and fails the replay if the origin's authority
    // changed in between — old intent never dispatches under a successor
    // session.
    fieldwatchExpectedGeneration?: string | null;
  }
}

const apiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Join a relative API path onto the api-client's configured base URL (the API
 * origin), mirroring how apiClient resolves request URLs. When no base URL is
 * configured (same-origin dev), the relative path is returned unchanged. This is the
 * sanctioned way for consumers that fetch OUTSIDE the apiClient instance (e.g. a
 * MapLibre raster source) to reach the API origin without hardcoding the env URL —
 * api-client.ts owns that env knowledge.
 */
export function resolveApiClientUrl(path: string): string {
  const baseURL = apiClient.defaults.baseURL;
  if (!baseURL) return path;
  return `${baseURL.replace(/\/+$/, '')}${path}`;
}

apiClient.interceptors.request.use((config) => {
  // Generation fence for refresh replays (see fieldwatchExpectedGeneration).
  // The adoption gate in the refresh interceptor is a check, not a
  // transaction — a replacement can commit between it and this dispatch. ONE
  // generation-bound read both validates the fence AND selects the bearer:
  // validating with one read and stamping from a second ambient read could be
  // torn apart by a replacement landing in between (fence sees the old
  // generation, the bearer read sees the new one — old intent would dispatch
  // under the replacement's bearer). The bearer stamped here always belongs
  // to the expected generation, or the replay is refused.
  if (config.fieldwatchExpectedGeneration !== undefined) {
    if (config.fieldwatchExpectedGeneration === null) {
      // LEGACY replay: authority must still be legacy, and the bearer is the
      // one the adoption already STAMPED on this config (the refresh
      // response's own token) — never an ambient storage read. The legacy
      // "authority" is composed from three keys (envelope, marker, raw
      // token), and no finite re-read makes that composition atomic: a
      // replacement committing between those reads could otherwise slip ITS
      // raw compatibility mirror in as the bearer. With the stamped bearer,
      // a validation that races a replacement still dispatches the OLD
      // principal's own token, never the successor's.
      const authority = getSessionRefreshAuthority();
      if (authority.state !== 'legacy' || !config.headers.Authorization) {
        throw new Error(
          'fieldwatch: replay refused (session authority changed before dispatch)',
        );
      }
      return config;
    }
    const bearer = getAccessTokenForGeneration(config.fieldwatchExpectedGeneration);
    if (bearer === null) {
      // A null bearer refuses the replay outright: a stamped replay whose
      // authority changed mid-flight must produce ZERO dispatch, not an
      // unauthenticated re-dispatch of the old intent.
      throw new Error(
        'fieldwatch: replay refused (session authority changed before dispatch)',
      );
    }
    config.headers.Authorization = `Bearer ${bearer}`;
    return config;
  }
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // A replayed config arrives with a stamped Authorization; when the current
  // authority yields no bearer (logged out, or present-but-invalid envelope),
  // that stamp must not survive — the request dispatches unauthenticated or
  // not at all, never under a resurrected header. (The C2 fixed-principal
  // transform is downstream of this and installs its own validated header.)
  else if (config.headers.Authorization) delete config.headers.Authorization;
  return config;
});

// Token refresh state — prevents concurrent refresh requests. Every queued
// request carries the session generation that was current when it queued
// (null = legacy pre-envelope custody), so a release can never hand a bearer
// to a request that belongs to a different session generation.
let isRefreshing = false;
let failedQueue: Array<{
  sessionId: string | null;
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function rejectQueue(error: unknown) {
  const entries = failedQueue;
  failedQueue = [];
  entries.forEach((prom) => prom.reject(error));
}

function releaseQueue(adoptedSessionId: string | null, token: string) {
  const entries = failedQueue;
  failedQueue = [];
  entries.forEach((prom) => {
    if (prom.sessionId === adoptedSessionId) prom.resolve(token);
    else prom.reject(new Error('fieldwatch: queued request refused (session generation drift)'));
  });
}

/**
 * Two authority observations agree only when they are the same custody model
 * and, for envelope custody, the same session generation. REFUSED never
 * agrees with anything — including itself — so an authority that cannot be
 * read never validates an adoption.
 */
function sameAuthority(a: SessionRefreshAuthority, b: SessionRefreshAuthority): boolean {
  if (a.state === 'active' && b.state === 'active') return a.sessionId === b.sessionId;
  return a.state === 'legacy' && b.state === 'legacy';
}

// ─── Rate-limit (429) retry interceptor ─────────────────────────
apiClient.interceptors.response.use(undefined, async (error) => {
  const config = error.config;
  if (
    error.response?.status === 429 &&
    config &&
    !config.fieldwatchSkipRateLimitRetry &&
    !config._rateLimitRetried
  ) {
    config._rateLimitRetried = true;
    const retryAfter = error.response.headers?.['retry-after'];
    // Respect Retry-After header (seconds), default to 1s, cap at 30s
    const delayMs = retryAfter
      ? Math.min(Number(retryAfter) * 1000, 30_000)
      : 1000;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    return apiClient(config);
  }
  return Promise.reject(error);
});

// ─── Auth refresh interceptor ───────────────────────────────────
apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    // Only attempt refresh on 401, and not on login/refresh endpoints
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.fieldwatchSkipAuthRefresh &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      if (isRefreshing) {
        // Queue this request until the in-flight refresh completes, tagged
        // with the generation current NOW. Under refused (present-but-invalid)
        // authority nothing may ever replay this request with a bearer, so it
        // fails here instead of queueing.
        const queuedAuthority = getSessionRefreshAuthority();
        if (queuedAuthority.state === 'refused') return Promise.reject(error);
        const queuedGeneration =
          queuedAuthority.state === 'active' ? queuedAuthority.sessionId : null;
        return new Promise((resolve, reject) => {
          failedQueue.push({
            sessionId: queuedGeneration,
            resolve: (token: string) => {
              // The fence field makes the request interceptor re-validate this
              // generation immediately before dispatch.
              originalRequest.fieldwatchExpectedGeneration = queuedGeneration;
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(apiClient(originalRequest));
            },
            reject: (err: unknown) => reject(err),
          });
        });
      }

      originalRequest._retry = true;

      // Authority at INITIATION — the generation every later step of this
      // refresh must re-validate against. A present-but-invalid envelope
      // refuses the whole path right here: no raw refresh-token read, no
      // refresh call, no queue release, no replay — and no storage mutation
      // either (an unreadable authority is evidence, not cleanup license).
      const initiating = getSessionRefreshAuthority();
      if (initiating.state === 'refused') return Promise.reject(error);

      isRefreshing = true;

      // Custody read is generation-bound: an active session accepts only THIS
      // generation's addressed custody — never the untagged raw key. A
      // replacement committing before this read yields null here rather than
      // the replacement's token, so its custody can never be burned by the
      // replaced session's refresh.
      const refreshToken = getRefreshToken(
        initiating.state === 'active' ? initiating.sessionId : null,
      );
      if (!refreshToken) {
        isRefreshing = false;
        // An ACTIVE generation without addressed custody is either an
        // establishment window (the same-realm custody write is still ahead
        // in another tab's login task) or an interrupted login. Neither
        // justifies tearing the session down: fail closed, surface the 401.
        // Only the never-adopted LEGACY path may treat missing raw custody as
        // the end of the session — and even then through the
        // generation-scoped end, never an ambient clearTokens(): a
        // replacement committing between any authority check and an ambient
        // teardown would be deleted by it. The redirect happens only when no
        // replacement has taken over.
        if (initiating.state === 'legacy' && endSessionForGeneration(null)) {
          if (typeof window !== 'undefined') window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(
          `${process.env.NEXT_PUBLIC_API_URL}/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } }
        );

        // Adoption gate: the response may be adopted ONLY under the same
        // authority that initiated it. If a replacement session committed (or
        // the authority became unreadable) while the refresh was in flight,
        // the response's credentials are stale: no access/refresh custody
        // update, no queue release, no replay — and the CURRENT session is
        // left untouched, because a refusal caused by replacement drift must
        // never clear or overwrite the replacement that outran this refresh.
        // Legacy custody has no generation to compare, so its gate is the
        // custody itself: a completed legacy logout removed the raw refresh
        // token mid-flight, and adopting past that would resurrect the
        // logged-out origin.
        const settled = getSessionRefreshAuthority();
        const legacyCustodyHolds = initiating.state === 'active' || getRefreshToken(null) !== null;
        if (!sameAuthority(initiating, settled) || !legacyCustodyHolds) {
          const refusal = new Error(
            'fieldwatch: stale refresh response refused (session authority changed mid-refresh)',
          );
          rejectQueue(refusal);
          return Promise.reject(refusal);
        }

        // Rotation, NOT establishment: a successful refresh continues the same
        // authenticated session, so it must not advance the feedback-session
        // identity the way a new login (setToken) does. Both custody writes
        // are bound to the INITIATING generation (null = legacy custody) — the
        // gate above is a check, not a transaction, and a replacement
        // committing after it leaves only inert stale-tagged records, never a
        // restored identity or clobbered custody.
        const adoptedGeneration = initiating.state === 'active' ? initiating.sessionId : null;
        rotateAccessToken(data.access_token, adoptedGeneration);
        setRefreshToken(data.refresh_token, adoptedGeneration);
        releaseQueue(adoptedGeneration, data.access_token);

        // The fence field makes the request interceptor re-validate the
        // adopted generation immediately before the replay dispatches.
        originalRequest.fieldwatchExpectedGeneration = adoptedGeneration;
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        rejectQueue(refreshError);
        // A failed refresh ends only the generation that INITIATED it, and
        // only through the generation-scoped end: an ambient clearTokens()
        // after an authority check is a check-then-remove that can delete a
        // replacement committing in between. The active end is a blind
        // generation-addressed tombstone (physically unable to touch any
        // other generation); the redirect happens only when the ended
        // generation is still what the origin holds, so a replacement that
        // outran the failure keeps both its session and its surface.
        if (sameAuthority(initiating, getSessionRefreshAuthority())) {
          const stillCurrent = endSessionForGeneration(
            initiating.state === 'active' ? initiating.sessionId : null,
          );
          if (stillCurrent && typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// ── Error-message extraction ────────────────────────────────────────────
//
// Every mutation hook replicated this exact shape before 2026-04-19:
//
//     toast.error(err?.response?.data?.detail || 'İşlem başarısız oldu')
//
// Across ~46 sites with at least three divergent variants:
//   * `||` vs `??` — backend returning an empty-string detail behaves
//     differently per site (one shows blank toast, another shows the
//     fallback). Fixed here by `||` — empty string falls through to
//     the caller-supplied fallback.
//   * Optional-chaining depth varied (some sites missed the outer
//     optional and crashed when axios raised a connection error
//     before any response arrived).
//   * Fallback messages were inconsistent (``İşlem başarısız``,
//     ``İşlem başarısız oldu``, ``Bir hata oluştu``, …). The caller
//     decides — this helper just enforces the shape.
//
// ADR-010's rule is "error handling at service/hook boundary only" —
// this helper is that boundary for the frontend.

interface ApiErrorShape {
  response?: { data?: { detail?: string } };
  message?: string;
}

/**
 * Extract the user-facing error message from an axios / fetch error.
 *
 * Lookup order:
 *   1. `error.response.data.detail` — FastAPI ProblemDetail path.
 *   2. `error.message` — axios network / timeout errors.
 *   3. Caller-supplied fallback.
 *
 * Empty strings fall through to the next level (``||`` semantics)
 * so a backend returning ``{detail: ""}`` does not produce a blank
 * toast.
 */
export function extractApiError(
  err: unknown,
  fallback = 'İşlem başarısız oldu',
): string {
  const e = err as ApiErrorShape;
  return e?.response?.data?.detail || e?.message || fallback;
}

/**
 * Shorthand for the common mutation-failure path — calls
 * ``toast.error`` with the best-available message. Import ``toast``
 * in the caller; we don't to keep this module tree-shakeable and
 * avoid a cycle with components that also use sonner.
 */
export function toastApiError(
  err: unknown,
  toast: { error: (msg: string) => unknown },
  fallback?: string,
): void {
  toast.error(extractApiError(err, fallback));
}

export default apiClient;
